<template lang="pug">
  #member-registration(:class="{ 'vh-height': !registrationAllowed }")
    .columns.is-centered.is-mobile
      .column.box.is-half-desktop.is-8-tablet.confirmation-message.animated.fadeIn(v-if="!registrationAllowed")
        .columns.is-centered.is-mobile
          .column.is-narrow
            figure.image.is-128x128
              img(:src="require('@/assets/icons/logo.svg')")
        BNotification(:closable="false")
          | We're sorry, registrations are turned off for this program.
          | Please contact your program administrator {{ adminEmail }} for more details.
      .column.box.is-two-thirds-tablet.is-paddingless.animated.fadeIn#top(v-else)
        .block(v-if="step === 1")
          h1.my-6.is-size-1.has-text-centered.is-family-secondary.has-text-darkerpurple {{ programRegistrationFields.name }}
            .columns
              .column.column.px-6.py-0
                h2.is-family-secondary.is-size-1.has-text-darkerpurple Create a New Account
                .notification.is-warning.is-size-6(v-if="userGuid !== null") You need to create an account before you can proceed any further.
                span.has-text-grey.is-size-6 All Fields are required.
                br
                span.has-text-danger.is-size-6 {{formErrorMessage}}
            .columns
              .column.p-4
                VclForm(v-if="!registrationFieldsLoaded")
                form(v-else @submit.prevent="submit")
                  .columns.is-multiline

                    //- FIRST NAME
                    .column.is-half
                      BField(
                        :type="{'is-danger': errors.has(firstName.fieldName)}"
                        :message="errors.first(firstName.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          :text="'First Name'"
                          :label-for="firstName.fieldName"
                        )
                        BInput(
                          :name="firstName.fieldName"
                          :id="firstName.fieldName"
                          v-validate="{ required: true, regex: /^[a-z ,.'-]+$/i }"
                          placeholder="First Name"
                          v-model="firstName.value"
                        )

                    //- LAST NAME
                    .column.is-half
                      BField(
                        :type="{'is-danger': errors.has(lastName.fieldName)}"
                        :message="errors.first(lastName.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          :text="'Last Name'"
                          :label-for="lastName.fieldName"
                        )
                        BInput(
                          :name="lastName.fieldName"
                          :id="lastName.fieldName"
                          v-validate="{ required: true, regex: /^[a-z ,.'-]+$/i }"
                          placeholder="Last Name"
                          v-model="lastName.value"
                        )

                    //- USERNAME
                    .column.is-half
                      BField(
                        :type="{'is-danger': errors.has(username.fieldName)}"
                        :message="errors.first(username.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          :text="'User Name'"
                          :label-for="username.fieldName"
                        )
                        BInput(
                          :id="username.fieldName"
                          v-model="username.value"
                          placeholder="username01"
                          v-validate="'required'"
                          :name="username.fieldName"
                        )

                    //- EMAIL
                    .column.is-half
                      BField(
                        :type="{'is-danger': errors.has(email.fieldName)}"
                        :message="errors.first(email.fieldName)"
                      )
                        WtLabel(
                          slot="label"
                          :text="'Email Address'"
                          :label-for="email.fieldName"
                        )
                        BInput(
                          :id="email.fieldName"
                          v-model="email.value"
                          type="email"
                          placeholder="name@email.com"
                          v-validate="'required|email'"
                          :name="email.fieldName"
                        )
                  .columns.is-multiline
                    //- PASSWORD
                    .column.is-half
                      BField(
                       :type="{'is-danger': errors.has(password.fieldName)}"
                       :message="errors.first(password.fieldName)"
                       :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          :text="'Password'"
                          :label-for="password.fieldName"
                        )
                        BInput(
                          ref="password"
                          :name="password.fieldName"
                          :id="password.fieldName"
                          type="password"
                          v-validate="'required'"
                          placeholder="*********"
                          v-model="password.value"
                          password-reveal
                          autocomplete="off"
                        )
                        PasswordComplexityCheck(:password="password.value")
                    .column.is-half
                      BField(
                        label="Confirm Password"
                        :label-for="passwordConfirm.fieldName"
                        :type="{'is-danger': errors.has(passwordConfirm.fieldName)}"
                        :message="errors.first(passwordConfirm.fieldName)"
                      )
                        BInput(
                          :name="passwordConfirm.fieldName"
                          :id="passwordConfirm.fieldName"
                          type="password"
                          v-validate="'required|confirmed:password'"
                          data-vv-as="password"
                          placeholder="*********"
                          v-model="passwordConfirm.value"
                          password-reveal
                          autocomplete="off"
                        )
                    //- EMPLOYEE ID
                    .column.is-half(v-if="programRegistrationFields.requireEmployeeId")
                      BField(
                        :type="{'is-danger': errors.has(employeeId.fieldName)}"
                        :message="errors.first(employeeId.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          :text="programRegistrationFields.employeeIdTitle"
                          :tooltip-text="programRegistrationFields.employeeIdDescription"
                          :label-for="employeeId.fieldName"
                          required
                        )
                        BInput(
                          :name="employeeId.fieldName"
                          :id="employeeId.fieldName"
                          type="text"
                          v-validate="'required'"
                          :placeholder="programRegistrationFields.employeeIdTitle"
                          v-model="employeeId.value"
                        )

                    //- TEAMS
                    .column.is-full(v-if="displayTeamJoiner && memberCanJoinTeam")
                      BField(
                        :type="{'is-danger': errors.has(team.fieldName)}"
                        :message="errors.first(team.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          text="Select Your Team"
                          :label-for="team.fieldName"
                          :required="!programRegistrationFields.teamJoiningOptional"
                        )
                        BAutocomplete(
                          :name="team.fieldName"
                          v-model="teamNameSearch"
                          placeholder="Team name search, and click to select"
                          :keep-first="true"
                          :open-on-focus="true"
                          :data="allOpenProgramTeamsFiltered"
                          field="name"
                          :loading="!programTeamsLoaded || teamSearching"
                          @select="option => { if (option && option.numMembers < option.maxMembers && !option.inviteOnly) team.value = option; }"
                          v-validate="programRegistrationFields.teamJoiningOptional ? '' : 'required'"
                        )
                          template(slot-scope="props")
                            article.media(
                              @click="$event => { if (props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly) $event.stopPropagation(); }"
                              :class="{ 'is-disabled': props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly }"
                              :style="{ opacity: props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly ? '0.6' : '1', cursor: props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly ? 'not-allowed' : 'pointer' }"
                            )
                              figure.media-left
                                p.image.is-48x48
                                  img.is-rounded(
                                    :src="_.get(props.option, 'logo', 'https://cdn.tbybewell.com/images/default-avatar.png')"
                                    alt="avatar"
                                    @error="teamAvatarOnError"
                                  )
                              .media-content
                                .content
                                  .has-text-weight-semibold {{ props.option.name }}
                                  small.has-text-danger(v-if="props.option.numMembers >= props.option.maxMembers && !props.option.inviteOnly") Team Full
                                  small(:style="{ color: '#2972F0' }" v-if="props.option.inviteOnly") (invite only)
                                  small.has-text-grey(v-if="!props.option.inviteOnly && props.option.numMembers < props.option.maxMembers") {{ Math.max(0, props.option.maxMembers - props.option.numMembers) }} open seat{{ Math.max(0, props.option.maxMembers - props.option.numMembers) === 1 ? '' : 's' }}

                      BField(label="Selected" v-if="_.get(team, 'value.id', false)")
                        article.media
                          figure.media-left
                            p.image.is-48x48
                              img.is-rounded(
                                :src="_.get(team.value, 'logo', 'https://cdn.tbybewell.com/images/default-avatar.png')"
                                alt="avatar"
                                @error="teamAvatarOnError"
                              )
                          .media-content
                            .content
                              .has-text-weight-semibold {{ team.value.name }}
                    //- ORGANIZATIONS
                    .column.is-full(v-if="programRegistrationFields.organizationsOn && isProTier")
                      BField(
                        :type="{'is-danger': errors.has(organization.fieldName)}"
                        :message="errors.first(organization.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          text="Select Your Organization"
                          :label-for="organization.fieldName"
                          required
                        )
                        BAutocomplete(
                          :name="organization.fieldName"
                          v-model="organizationNameSearch"
                          placeholder="Organization name search, and click to select"
                          :keep-first="true"
                          :open-on-focus="true"
                          :data="allProgramOrganizationsFiltered"
                          field="name"
                          :loading="!programOrganizationsLoaded"
                          @select="option => { if (option && option.active) selectOrganization(option); }"
                          v-validate="{ required: true, included: organizationNames }"
                        )
                          template(slot-scope="props")
                            article.media(
                              @click="$event => { if (!props.option.active) $event.stopPropagation(); }"
                              :class="{ 'is-disabled': !props.option.active }"
                              :style="{ opacity: !props.option.active ? '0.6' : '1', cursor: !props.option.active ? 'not-allowed' : 'pointer' }"
                            )
                              figure.media-left
                                p.image.is-48x48
                                  img.is-rounded(
                                    :src="_.get(props.option, 'logo', 'https://cdn.tbybewell.com/images/default-avatar.png')"
                                    alt="avatar"
                                    @error="teamAvatarOnError"
                                  )
                              .media-content
                                .content
                                  .has-text-weight-semibold {{ props.option.name }}
                                  small.has-text-danger(v-if="!props.option.active") Organization Off
                    //- ORGANIZATION TEAMS
                    .column.is-full(v-if="displayOrganizationTeamJoiner")
                      BField(
                        :type="{'is-danger': errors.has(team.fieldName)}"
                        :message="errors.first(team.fieldName)"
                        :addons="false"
                      )
                        WtLabel(
                          slot="label"
                          :text="`Select Your Team: ${organization.value.name}`"
                          :label-for="team.fieldName"
                          :required="!programRegistrationFields.teamJoiningOptional"
                        )
                        BAutocomplete(
                          :name="team.fieldName"
                          v-model="teamNameSearch"
                          placeholder="Team name search, and click to select"
                          :keep-first="true"
                          :open-on-focus="true"
                          :data="allOrganizationTeamsFiltered"
                          field="name"
                          @select="option => { if (option && option.numMembers < option.maxMembers && !option.inviteOnly) team.value = option; }"
                          v-validate="programRegistrationFields.teamJoiningOptional ? '' : `required|included:${teamNames}`"
                        )
                          template(slot-scope="props")
                            article.media(
                              @click="$event => { if (props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly) $event.stopPropagation(); }"
                              :class="{ 'is-disabled': props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly }"
                              :style="{ opacity: props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly ? '0.6' : '1', cursor: props.option.numMembers >= props.option.maxMembers || props.option.inviteOnly ? 'not-allowed' : 'pointer' }"
                            )
                              figure.media-left
                                p.image.is-48x48
                                  img.is-rounded(
                                    :src="_.get(props.option, 'logo', 'https://cdn.tbybewell.com/images/default-avatar.png')"
                                    alt="avatar"
                                    @error="teamAvatarOnError"
                                  )
                              .media-content
                                .content
                                  .has-text-weight-semibold {{ props.option.name }}
                                  small.has-text-danger(v-if="props.option.numMembers >= props.option.maxMembers && !props.option.inviteOnly") Team Full
                                  small(:style="{ color: '#2972F0' }" v-if="props.option.inviteOnly") (invite only)
                                  small.has-text-grey(v-if="!props.option.inviteOnly && props.option.numMembers < props.option.maxMembers") {{ Math.max(0, props.option.maxMembers - props.option.numMembers) }} open seat{{ Math.max(0, props.option.maxMembers - props.option.numMembers) === 1 ? '' : 's' }}
                    .column.is-full(v-if="programRegistrationFields.teamJoinerOn && !createTeamAfterSignIn && programRegistrationFields.organizationsOn && isProTier && organization.value")
                      .content.is-size-7.has-text-grey(v-if="!organizationTeams.length") No teams in this organization
                  .create-team(v-if="displayCreateTeamButton")
                    .column.is-full(v-if="!createTeamAfterSignIn")
                      .is-size-5 or
                      .create-team-button.is-size-5(
                        @click="createTeamModal = true"
                      ) Create a new team
                    .column.is-full(v-else)
                      span.is-size-5 Team: {{newTeamName}}
                      span.is-size-5.create-team-button(@click="resetCreateTeamAfterSignIn") (Reset)
                  .columns.is-multiline
                    .column.is-full.pt-4
                      .content.has-text-grey.is-size-7
                        | Please see our #[a.is-link(href="https://www.terryberry.com/terms-and-conditions/" rel="noopener noreferrer" target="_blank") Terms of Service] and
                        | #[a.is-link(href="https://www.terryberry.com/privacy-policy/" rel="noopener noreferrer" target="_blank") Privacy Policy].  Clicking 'Register'
                        | indicates your acceptance.  Your program administrator may send you
                        | email using this system.  You will have the opportunity to opt out
                        | of emails in your settings after registration.
                    .column.mx-auto.is-flex-direction-column.has-text-centered
                      WtButton.is-family-secondary.register-button(type="submit" :loading="registering") Register Now
                  BModal(
                    :active="createTeamModal"
                    has-modal-card
                    trap-focus
                    :on-cancel="dismissCreateTeamModal"
                  )
                    WtTile(child).team-builder-container
                      TeamBuilder(
                        :registrationBuilder="true"
                        :registrationData="teamBuilderInfo"
                        :dismissModal="dismissCreateTeamModal"
                        :organization="organization"
                      )
        .block(v-if="step === 2")
          h1.mt-6.mb-3.is-size-1.has-text-centered.is-family-secondary.has-text-darkerpurple Welcome!
          h2.is-size-4.has-text-darkerpurple.has-text-weight-bold.has-text-centered.px-6.py-0
            | {{getAdminQuestionsPrompt()}}

          VclForm(v-if="!registrationFieldsLoaded")
          .column.p-6.is-two-thirds-desktop(v-else)
              div(v-for="q in sortedQuestions" :key="q.id" class="my-5")
                WtLabel.is-size-5(
                  slot="label"
                  :text="q.question"
                  :required="q.required"
                )
                .is-size-7.has-text-grey.question-description(v-html="q.description")
                QuestionAnswers(:question="q" @onAnswer="collectAnswer($event)")
          .column.mx-auto.is-flex-direction-column.has-text-centered
            WtButton.is-family-secondary.register-button(
              type="button"
              :loading="registering"
              :disabled="!allRequiredQuestionsAnswered"
              @click.native="answerQuestions"
            ) Finish
        .block.has-text-centered.px-6(v-if="step === 3")
          h1.mt-6.mb-3.is-size-1.has-text-centered.is-family-secondary.has-text-darkerpurple Thanks for registering!
          h2.is-size-4.has-text-darkerpurple.has-text-weight-bold.py-0
            | Check your email for a welcome letter. Enjoy!
          WtButton.is-family-secondary.register-button.mt-6(
            type="button"
            @click.native="login"
          ) Take me to the app
        a(href="https://www.terryberry.com/employee-wellness-programs" target="_blank" rel="noopener noreferrer").is-flex.is-justify-content-center.is-align-items-flex-end.m-5
          p.is-family-secondary.is-size-7.has-text-darkerpurple.mb-4.has-text-right Powered <br /> by
          figure.image.is-96x96.p-2
            img(:src="require('@/assets/icons/be-well-logo.svg')")
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import VclForm from "@@/VclRegForm/VclRegForm";
import WtLabel from "@@/WtLabel/WtLabel";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";
import PasswordComplexityCheck from "@@/PasswordComplexityCheck.vue";
import QuestionAnswers from "@@/ProgramQuestions/QuestionAnswers";
import TeamBuilder from "@@/TeamBuilder/TeamBuilder";
import WtTile from "@@/WtTile/WtTile";
import Vue from "vue";

export default {
  name: "MemberRegistration",

  components: {
    WtTile,
    WtTitle,
    WtButton,
    VclForm,
    WtLabel,
    PasswordComplexityCheck,
    QuestionAnswers,
    TeamBuilder
  },

  mixins: [dateUtilityMixin],

  data() {
    return {
      firstName: { fieldName: "first-name", value: "" },
      lastName: { fieldName: "last-name", value: "" },
      username: { fieldName: "username", value: "" },
      email: { fieldName: "email", value: "" },
      password: { fieldName: "password", value: "" },
      passwordConfirm: { fieldName: "password-confirm", value: "" },
      registeredMemberId: null,
      formDataQuestions: [],
      questions: [],
      error: false,
      formErrorMessage: null,
      registering: false,
      employeeId: { fieldName: "employee-id", value: "" },
      team: { fieldName: "team", value: "" },
      teamNameSearch: "",
      organization: { fieldName: "organization", value: "" },
      organizationNameSearch: "",
      organizationTeams: [],
      organizationTeamsFull: true,
      allOpenProgramTeams: [],
      teamSearchResults: null,
      teamSearching: false,
      teamSearchTimer: null,
      selectTeamOpen: false,
      step: 1,
      createTeamModal: false,
      allOrganizationTeams: [],
      userGuid: null
    };
  },

  computed: {
    ...mapGetters("registration", [
      "programRegistrationFields",
      "registrationFieldsLoaded",
      "createTeamAfterSignIn",
      "createTeamFormData"
    ]),
    ...mapGetters("program", [
      "programTeamsLoaded",
      "isCoreTier",
      "programOrganizationsLoaded",
      "programOrganizations",
      "isProTier"
    ]),
    ...mapGetters(["mobileBreakPoint"]),

    formData() {
      let payload = {
        firstName: this.firstName.value,
        lastName: this.lastName.value,
        username: this.username.value,
        email: this.email.value,
        password: this.password.value,
        password_confirmation: this.passwordConfirm.value,
        timezone: `-${new Date().getTimezoneOffset() / 60}`, // todo: this approach doesn't work for those with a positive GMT offset.
        questions: this.formDataQuestions,
        programId: this.programRegistrationFields.id,
        affiliation: "employee"
      };

      if (this.isSso && this.userGuid !== null) {
        payload = {
          ...payload,
          ...{ terryberryUserId: this.userGuid }
        };
      }
      if (this.programRegistrationFields.requireEmployeeId) {
        payload = { ...payload, ...{ employeeId: this.employeeId.value } };
      }
      if (
        this.programRegistrationFields.teamJoinerOn &&
        !this.createTeamAfterSignIn
      ) {
        payload = { ...payload, ...{ teamId: this.team.value.id } };
      }
      if (this.programRegistrationFields.organizationsOn && this.isProTier) {
        payload = {
          ...payload,
          ...{
            organizationId: this._.get(
              this.organization.value,
              "id",
              undefined
            ),
            teamId: this._.get(this.team.value, "id", undefined)
          }
        };
      }
      return payload;
    },

    adminEmail() {
      return this.programRegistrationFields.registrationTargetEmailTo !== ""
        ? `at ${this.programRegistrationFields.registrationTargetEmailTo}`
        : "";
    },

    isSso() {
      return this.userGuid != null;
    },

    registrationAllowed() {
      return (
        this._.get(this.programRegistrationFields, "allowRegistration", true) ||
        this.userGuid != null
      );
    },

    allOpenProgramTeamsFiltered() {
      return this.teamSearchResults !== null
        ? this.teamSearchResults
        : this.allOpenProgramTeams;
    },

    allProgramOrganizationsFiltered() {
      return this.programOrganizations.filter(organization =>
        organization.name
          .toLowerCase()
          .includes(this.organizationNameSearch.toLowerCase())
      );
    },

    allOrganizationTeamsFiltered() {
      return this.organizationTeams.filter(team =>
        team.name.toLowerCase().includes(this.teamNameSearch.toLowerCase())
      );
    },

    allRequiredQuestionsAnswered() {
      for (const question of this.sortedQuestions.filter(q => q.required)) {
        const answeredQuestion = this.formDataQuestions.find(
          q => q.id === question.id
        );
        if (
          !answeredQuestion ||
          (typeof answeredQuestion.answer === "string" &&
            answeredQuestion.answer.replace(/\s+/g, "") === "")
        )
          return false;
      }
      return true;
    },

    sortedQuestions() {
      const currentDate = this.today().formatted;

      // Filter and sort the questions without mutating the original array
      return [...this.questions]
        .filter(question => {
          const startDate =
            question.startDate == ""
              ? null
              : this.date(question.startDate).formatted;
          const endDate =
            question.endDate == ""
              ? null
              : this.date(question.endDate).formatted;
          switch (true) {
            case !startDate && !endDate:
              return question;
            case !startDate && endDate:
              return currentDate <= endDate;
            case startDate && !endDate:
              return currentDate >= startDate;
            case startDate != null && endDate != null:
              return currentDate >= startDate && currentDate <= endDate;
          }
        })
        .sort((a, b) => a.order - b.order);
    },

    organizationNames() {
      return this.programOrganizations.map(org => org.name);
    },

    teamNames() {
      return this.organizationTeams.map(team => team.name);
    },

    displayTeamJoiner() {
      // don't display the team joiner twice
      if (this.createTeamAfterSignIn) {
        return false;
      }
      return !!(
        this.programRegistrationFields.teamJoinerOn &&
        !(this.programRegistrationFields.organizationsOn && this.isProTier)
      );
    },

    displayCreateTeamButton() {
      return !!(
        !this.team.value &&
        this.programRegistrationFields.memberCanManageTeams &&
        this.programRegistrationFields.teamJoinerOn
      );
    },

    displayOrganizationTeamJoiner() {
      return (
        this.programRegistrationFields.teamJoinerOn &&
        !this.createTeamAfterSignIn &&
        this.programRegistrationFields.organizationsOn &&
        this.isProTier &&
        this.organization.value &&
        this.organizationTeams.length
      );
    },

    memberCanJoinTeam() {
      let allTeamsAreFull = true;
      this.allOpenProgramTeams.forEach(team => {
        if (team.maxMembers > team.numMembers) {
          allTeamsAreFull = false;
        }
      });
      return (
        // member can create team
        this.programRegistrationFields.memberCanManageTeams ||
        // there are teams available
        !allTeamsAreFull
      );
    },

    teamBuilderInfo() {
      return {
        programId: this.programRegistrationFields.id
      };
    },

    newTeamName() {
      if (this.createTeamAfterSignIn && this.createTeamFormData) {
        return this.createTeamFormData.get("name");
      }
      return "";
    },

    isMobileWebView() {
      return Vue.router.currentRoute.fullPath.includes("mobile");
    }
  },

  watch: {
    teamNameSearch(val) {
      // Only search via API when org mode is off (non-org team joiner)
      if (this.programRegistrationFields.organizationsOn && this.isProTier)
        return;
      clearTimeout(this.teamSearchTimer);
      if (!val) {
        this.teamSearchResults = null;
        this.teamSearching = false;
        return;
      }
      this.teamSearching = true;
      this.teamSearchTimer = setTimeout(async () => {
        const result = await this.searchProgramTeams({
          programId: this.programRegistrationFields.id,
          searchTerm: val,
          perPage: 100
        });
        this.teamSearchResults = result.data || [];
        this.teamSearching = false;
      }, 300);
    }
  },

  beforeRouteEnter(to, from, next) {
    const programName = window.location.hostname;

    let payload = { programName };

    const invite = to.query.invite;
    if (invite) {
      Object.assign(payload, { invite });
    }

    const programCode = to.query.programCode;
    if (programCode) {
      Object.assign(payload, { programCode });
    }

    next(async vm => {
      if (to.query.userGuid) {
        vm.userGuid = to.query.userGuid;
      }

      await vm.fetchRegistration(payload);
      vm.questions = vm._.get(
        vm.programRegistrationFields,
        "registrationQuestions",
        []
      );
      if (vm.programRegistrationFields.teamJoinerOn) {
        await vm
          .fetchProgramTeams({
            all: 1,
            perPage: 1000,
            programId: vm.programRegistrationFields.id
          })
          .then(response => {
            // Show all teams (including invite-only and full ones)
            vm.allOpenProgramTeams = response.data;
          });
      }

      if (vm.programRegistrationFields.organizationsOn && vm.isProTier) {
        await vm.fetchProgramOrganizations({
          programId: vm.programRegistrationFields.id
        });
      }
    });
  },

  methods: {
    ...mapActions("registration", [
      "fetchRegistration",
      "register",
      "resetCreateTeamAfterSignIn",
      "mobileLogin"
    ]),
    ...mapActions("program", [
      "fetchProgramTeams",
      "searchProgramTeams",
      "saveUserAnswers",
      "fetchProgramOrganizations"
    ]),
    ...mapActions("teams", ["createTeam"]),
    ...mapActions(["resetState"]),

    async submit() {
      this.registering = true;
      const validationResult = await this.$validator.validateAll();

      if (!validationResult) {
        this.registering = false;
        this.$scrollTo("#top");
        return;
      }
      if (this.createTeamAfterSignIn && this.formData.teamId) {
        delete this.formData.teamId;
      }
      // if no organization teams and team joining is not optional user must create a team
      if (
        this.programRegistrationFields.teamJoinerOn &&
        !this.programRegistrationFields.teamJoiningOptional &&
        this.isProTier &&
        !(this.team.value || this.createTeamAfterSignIn) &&
        // make sure there is a team to join or user can create team
        (this.organizationTeams.length ||
          this.programRegistrationFields.memberCanManageTeams)
      ) {
        this.error = true;
        this.formErrorMessage = "You must join a team or create a team";
        this.$scrollTo("#top");
        this.registering = false;
        return;
      } else {
        this.error = false;
      }
      const response = await this.register(this.formData);

      if (response.error) {
        this.error = true;
        this.formErrorMessage = this.errorMessage(response.error);
        this.$scrollTo("#top");
        this.registering = false;
      } else if (this.sortedQuestions.length) {
        this.registeredMemberId = response.data.id;
        this.registering = false;
        this.step = 2;
      } else {
        if (this.isMobileWebView) {
          this.step = 3;
        } else {
          await this.login();
        }
      }
    },

    async answerQuestions() {
      this.registering = true;
      // filter out blank answers
      const validAnswers = this.formDataQuestions.filter(question => {
        if (typeof question.answer === "string") {
          const answer = question.answer.replace(/\s+/g, "");
          return answer !== "";
        } else {
          const answer = question.answer;
          return answer;
        }
      });
      if (this.formDataQuestions.length) {
        await this.saveUserAnswers({
          programId: Number(this.programRegistrationFields.id),
          memberId: Number(this.registeredMemberId),
          questionAnswers: validAnswers
        });
      }
      if (this.isMobileWebView) {
        this.step = 3;
      } else {
        await this.login();
      }
    },

    async login() {
      let params = {
        username: this.username.value,
        password: this.password.value,
        programId: this.programRegistrationFields.id,
        isPartner: 0
      };

      // if is a mobile web view display success message, and take the user back to the app
      if (this.isMobileWebView) {
        try {
          params.isMobile = 1;
          let userAgent = navigator.userAgent;
          if (
            window.innerWidth < this.mobileBreakPoint &&
            /iPhone|Android/i.test(userAgent)
          ) {
            let magiclinkParams = {
              email: this.email.value
            };
            let response = await this.axios.post(
              "onetime-magiclink",
              magiclinkParams
            );
            window.location.href = response.data.data.magicLink;
          } else {
            alert("Sorry - mobile registration does not work on your device.");
          }
        } catch (error) {
          this.error = true;
          this.formErrorMessage = error.response.data.error.message;
          this.registering = false;
          this.$scrollTo("#top");
        }
        return;
      }

      try {
        await this.$auth.login({ data: params });
        if (this.createTeamAfterSignIn) {
          let formDataCopy = this.createTeamFormData;
          let organizationId = this._.get(
            this.organization.value,
            "id",
            undefined
          );
          if (organizationId) {
            formDataCopy.set("organizationId", organizationId);
          }
          await this.createTeam(formDataCopy);
          this.resetCreateTeamAfterSignIn();
        }
      } catch (error) {
        this.error = true;
        this.formErrorMessage = error.response.data.error.message;
        this.registering = false;
        this.$scrollTo("#top");
      }
    },

    errorMessage(errorObj) {
      if (errorObj.fields) {
        return this._.first(this._.map(errorObj.fields))[0];
      }

      return errorObj.message;
    },

    collectAnswer(event) {
      const questionResult = {
        id: event.questionId,
        answer: this._.isArray(event.answer)
          ? event.answer.join(",")
          : event.answer
      };

      const arrayQuestionResult = this._.find(this.formDataQuestions, {
        id: questionResult.id
      });

      if (arrayQuestionResult) {
        arrayQuestionResult.answer = questionResult.answer;
      } else {
        this.formDataQuestions.push(questionResult);
      }
    },

    teamAvatarOnError(event) {
      event.target.onerror = null;
      event.target.src = "https://cdn.tbybewell.com/images/default-avatar.png";
    },

    selectOrganization(option) {
      this.organization.value = option;
      this.allOrganizationTeams = this._.get(option, "teams", []);
      this.organizationTeams = this.allOrganizationTeams;
      this.organizationTeamsFull = !this.organizationTeams.some(
        t => !t.inviteOnly && t.numMembers < t.maxMembers
      );
      if (this.organizationTeamsFull) {
        this.team.value = "";
        this.teamNameSearch = "";
      }
    },

    // TODO:
    // verification token needs to be parsed via APIv3
    // recaptcha() {
    //   this.$recaptcha("registration").then(async token => {
    //     console.log(token);
    //   });
    // }
    dismissCreateTeamModal() {
      this.createTeamModal = false;
    },

    getAdminQuestionsPrompt() {
      if (this.sortedQuestions.length > 1) {
        return "Please answer few questions from your program administrator to continue";
      }
      return "Please answer a question from your program administrator to continue";
    }
  }
};
</script>

<style lang="sass" scoped>
#member-registration
  min-height: 100%
  background-color: $white-ter
  .confirmation-message
    padding: 1.25rem
  .columns
    margin-left: 0 !important
    margin-right: 0 !important
  #top
    border-radius: $radius-large
    max-width: 100%
  .register-button
    padding-left: 120px
    padding-right: 120px
    margin: 20px
    font-size: 1.1rem
    +mobile
      padding-left: 0
      padding-right: 0
      width: 100%
      margin: 20px 0 8px 0
  .is-link
    text-decoration: underline
  .team-builder-container
    background-color: $grey-lightest
    text-align: left
    font-size: initial
    width: 90vw
    max-width: 960px
  .create-team-button
    color: $primary
    &:hover
      cursor: pointer
.vh-height
  height: 100vh
</style>
